import React from "react";
import {
  countyBorderPath,
  municipalityPoints,
  outlinePath,
  viewBox,
} from "./swedenMap";

export interface MapMunicipality {
  code: string;
  name: string;
}

interface MunicipalityMapProps {
  municipalities: MapMunicipality[];
  /** Tom när kampanjstartsidan saknar statistiksida; prickarna blir då inte länkar. */
  statisticsUrl: string;
  selectedCode?: string;
}

interface TooltipState {
  code: string;
  name: string;
  left: number;
  top: number;
}

// Den valda pricken skalas upp 1,5 gånger (se .breaks-map__dot--selected), radien är 12.
const SELECTED_DOT_TOP_OFFSET = 18;

export function municipalityUrl(statisticsUrl: string, code: string): string {
  const url = new URL(statisticsUrl, window.location.origin);
  url.searchParams.set("municipality", code);
  return url.pathname + url.search;
}

export default function MunicipalityMap({
  municipalities,
  statisticsUrl,
  selectedCode,
}: MunicipalityMapProps): React.JSX.Element {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);
  const [selectedLabel, setSelectedLabel] = React.useState<TooltipState | null>(null);

  // SVG:n skalas med skärmbredden, så etikettens pixelplats räknas om vid varje storleksändring.
  React.useLayoutEffect(() => {
    const selected = municipalities.find((m) => m.code === selectedCode);
    const point = selectedCode ? municipalityPoints[selectedCode] : undefined;
    const wrapper = wrapperRef.current;
    const svg = svgRef.current;
    if (!selected || !point || !wrapper || !svg) {
      setSelectedLabel(null);
      return;
    }

    const place = () => {
      // Kartan ritas smalare än SVG-rutan när max-height slår in; getScreenCTM tar hänsyn till det.
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      const labelPoint = new DOMPoint(
        point[0],
        point[1] - SELECTED_DOT_TOP_OFFSET,
      ).matrixTransform(ctm);
      setSelectedLabel({
        code: selected.code,
        name: selected.name,
        left: labelPoint.x - wrapperRect.left,
        top: labelPoint.y - wrapperRect.top,
      });
    };

    place();
    const observer = new ResizeObserver(place);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [municipalities, selectedCode]);

  // Koder som saknas i kartunderlaget (t.ex. nya kommuner) hoppas över.
  // SVG saknar z-index, så den valda pricken ritas sist för att hamna överst.
  const dots = municipalities
    .filter((m) => municipalityPoints[m.code])
    .sort((a, b) => Number(a.code === selectedCode) - Number(b.code === selectedCode));

  const showTooltip =
    (code: string, name: string) => (event: React.SyntheticEvent<Element>) => {
      const wrapper = wrapperRef.current?.getBoundingClientRect();
      if (!wrapper) return;
      const dot = event.currentTarget.getBoundingClientRect();
      setTooltip({
        code,
        name,
        left: dot.left + dot.width / 2 - wrapper.left,
        top: dot.top - wrapper.top,
      });
    };
  const hideTooltip = () => setTooltip(null);

  return (
    <div className="breaks-map" ref={wrapperRef}>
      <svg
        ref={svgRef}
        className="breaks-map__svg"
        viewBox={viewBox}
        role="group"
        aria-label="Karta över Sverige med deltagande kommuner"
      >
        <path className="breaks-map__outline" d={outlinePath} />
        <path className="breaks-map__borders" d={countyBorderPath} />
        {dots.map(({ code, name }) => {
          const [x, y] = municipalityPoints[code];
          return (
            <a
              key={code}
              className={
                code === selectedCode
                  ? "breaks-map__dot breaks-map__dot--selected"
                  : "breaks-map__dot"
              }
              href={statisticsUrl ? municipalityUrl(statisticsUrl, code) : undefined}
              aria-label={`${name} – se deltagande`}
              onMouseEnter={showTooltip(code, name)}
              onFocus={showTooltip(code, name)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
            >
              <circle className="breaks-map__dot-shape" cx={x} cy={y} r={12} />
            </a>
          );
        })}
      </svg>
      {selectedLabel && (
        <div
          className="breaks-map__tooltip"
          style={{ left: selectedLabel.left, top: selectedLabel.top }}
          aria-hidden="true"
        >
          {selectedLabel.name}
        </div>
      )}
      {tooltip && tooltip.code !== selectedCode && (
        <div
          className="breaks-map__tooltip"
          style={{ left: tooltip.left, top: tooltip.top }}
          aria-hidden="true"
        >
          {tooltip.name}
        </div>
      )}
    </div>
  );
}
